import { useMemo, useRef, useState } from "react";

import { SignIn } from "@/auth/SignIn";
import { useSession } from "@/auth/useSession";
import { Diagnostics } from "@/components/Diagnostics";
import { TopBar } from "@/components/TopBar";
import { TabBar } from "@/components/TabBar";
import type { Tab } from "@/components/TabBar";
import { usePush } from "@/push/usePush";
import { useSheet } from "@/store/useSheet";
import { Settings } from "@/screens/Settings/Settings";
import { Sheet } from "@/screens/Show/Sheet";
import { buildQueue } from "@/lib/episode";
import type { QueueEntry } from "@/lib/episode";
import type { SimklClient } from "@/api/client";
import type { LibraryShow, SearchResult } from "@/api/schemas";
import { Library } from "@/screens/Library/Library";
import { Search } from "@/screens/Search/Search";
import { Upcoming } from "@/screens/Upcoming/Upcoming";
import { UpNext } from "@/screens/UpNext/UpNext";
import { editFor } from "@/lib/optimistic";
import type { OutboxAction } from "@/store/outbox";
import { useLibrary } from "@/store/useLibrary";
import type { ShowDetail } from "@/store/details";
import { useShowDetails } from "@/store/useShowDetails";
import { useOutbox } from "@/store/useOutbox";
import { useAside } from "@/store/useAside";
import { useUpcoming } from "@/store/useUpcoming";

/** Five taps on the title opens the measurement panel. */
const TAPS_TO_DIAGNOSE = 5;

const TITLES: Record<Tab, string> = {
  upnext: "Up Next",
  upcoming: "Upcoming",
  library: "Library",
  search: "Search",
};

function headerCount(pending: number, tab: Tab, queued: number): string | null {
  if (pending > 0) return `${String(pending)} pending`;
  if (tab !== "upnext" || queued === 0) return null;
  return queued === 1 ? "1 show" : `${String(queued)} shows`;
}

/** Everything the app loads, in one place. */
function useAppData(session: ReturnType<typeof useSession>, ready: boolean, tab: Tab) {
  const library = useLibrary(session.client, ready);
  const outbox = useOutbox(session.client, ready);
  const details = useShowDetails(session.client, library.shows, ready);
  const upcoming = useUpcoming(session.client, library.shows, ready && tab === "upcoming");
  const queue = useMemo(() => buildQueue(library.shows), [library.shows]);
  const knownIds = useMemo(
    () => new Set(library.shows.map((show) => show.show.ids.simkl)),
    [library.shows],
  );
  const sheet = useSheet(library.shows);
  const aside = useAside();
  // Read during render rather than stored: the holder already has the token by
  // the time the session is ready, so an effect would only add a second render.
  const push = usePush(ready ? session.getToken() : null, library.shows, details);
  return { library, outbox, details, upcoming, queue, knownIds, sheet, push, aside };
}

/** The two panels that sit above everything: settings, and the measurements. */
function Overlays({
  push,
  settings,
  diag,
  onCloseSettings,
  onCloseDiag,
}: {
  push: ReturnType<typeof usePush>;
  settings: boolean;
  diag: boolean;
  onCloseSettings: () => void;
  onCloseDiag: () => void;
}) {
  return (
    <>
      {settings && <Settings push={push} onClose={onCloseSettings} />}
      {diag && <Diagnostics onClose={onCloseDiag} />}
    </>
  );
}

/**
 * Five taps on the title opens the measurement panel.
 *
 * A ref, not state: the count is not rendered, and five taps landing before a
 * re-render would all read the same state value and never reach five.
 */
function useDiagnosticsTap() {
  const taps = useRef(0);
  const [diag, setDiag] = useState(false);
  const onSecretTap = () => {
    taps.current += 1;
    if (taps.current >= TAPS_TO_DIAGNOSE) {
      taps.current = 0;
      setDiag(true);
    }
  };
  return { diag, setDiag, onSecretTap };
}

export function App() {
  const session = useSession();
  const ready = session.phase === "ready";
  const [tab, setTab] = useState<Tab>("upnext");
  const [settings, setSettings] = useState(false);
  const [librarySearch, setLibrarySearch] = useState<string | null>(null);
  const { diag, setDiag, onSecretTap } = useDiagnosticsTap();

  const data = useAppData(session, ready, tab);
  const { library, outbox, details, upcoming, queue, knownIds, sheet, push, aside } = data;
  // Locally first, so the tap lands immediately. Simkl owns the totals and
  // which episode comes next, so its answer replaces this one once the outbox
  // reports the write has actually gone through.
  const handleAction = (action: OutboxAction) => {
    const edit = editFor(action);
    if (edit !== null) library.patch(edit);
    // Watching an episode is the answer to "are you still on this?", so a show
    // set aside comes back to the queue on its own. Unmarking does not: taking
    // back a mistake is not the same as picking the show up again.
    if (edit !== null && edit.watched) aside.clear(edit.simklId);
    outbox.submit(action, library.refresh);
  };

  if (session.phase === "starting") return <div className="app" />;

  if (!ready) {
    return (
      <div className="app">
        <SignIn
          pin={session.pin}
          pairing={session.phase === "pairing"}
          error={session.error}
          onSignIn={session.signIn}
        />
      </div>
    );
  }

  return (
    <div className="app">
      <TopBar
        title={TITLES[tab]}
        count={headerCount(outbox.queued.length, tab, queue.length)}
        showSettings={tab === "search"}
        search={tab === "library" ? librarySearch : null}
        onSearch={tab === "library" ? setLibrarySearch : null}
        onSecretTap={onSecretTap}
        onSettings={() => {
          setSettings(true);
        }}
      />

      <main className="screens">
        <section className="screen active">
          <Screen
            tab={tab}
            library={library}
            queue={queue}
            upcoming={upcoming}
            onAction={handleAction}
            client={session.client}
            knownIds={knownIds}
            details={details}
            librarySearch={librarySearch}
            aside={aside}
            onOpen={sheet.open}
            onPreview={sheet.preview}
          />
        </section>
      </main>

      <TabBar active={tab} onSelect={setTab} />

      <Overlays
        push={push}
        settings={settings}
        diag={diag}
        onCloseSettings={() => {
          setSettings(false);
        }}
        onCloseDiag={() => {
          setDiag(false);
        }}
      />

      <Sheet
        sheet={sheet}
        client={session.client}
        pending={outbox.queued.length > 0}
        aside={aside}
        onAction={handleAction}
      />
    </div>
  );
}

function Screen({
  tab,
  library,
  queue,
  upcoming,
  onAction,
  client,
  knownIds,
  details,
  librarySearch,
  aside,
  onOpen,
  onPreview,
}: {
  tab: Tab;
  library: ReturnType<typeof useLibrary>;
  queue: readonly QueueEntry[];
  upcoming: ReturnType<typeof useUpcoming>;
  onAction: (action: OutboxAction) => void;
  client: SimklClient;
  knownIds: ReadonlySet<number>;
  details: ReadonlyMap<number, ShowDetail>;
  librarySearch: string | null;
  aside: ReturnType<typeof useAside>;
  onOpen: (simklId: number) => void;
  onPreview: (result: SearchResult) => void;
}) {
  if (library.error !== null) {
    return (
      <div className="state">
        <strong>Could not reach Simkl</strong>
        <p>{library.error}</p>
        <button type="button" onClick={library.refresh}>
          Try again
        </button>
      </div>
    );
  }
  if (tab === "upnext") {
    return (
      <UpNext
        queue={queue}
        loading={library.loading}
        asideIds={aside.ids}
        onAction={onAction}
        onOpen={(entry) => {
          onOpen(entry.show.show.ids.simkl);
        }}
      />
    );
  }
  if (tab === "upcoming") {
    return <Upcoming episodes={upcoming.episodes} loading={upcoming.loading} onOpen={onOpen} />;
  }
  if (tab === "library") {
    return (
      <Library
        shows={library.shows}
        loading={library.loading}
        details={details}
        query={librarySearch}
        onOpen={(show: LibraryShow) => {
          onOpen(show.show.ids.simkl);
        }}
      />
    );
  }
  return (
    <Search
      client={client}
      knownIds={knownIds}
      onAdd={(result) => {
        onAction({ kind: "add", simklId: result.ids.simkl, to: "watching" });
      }}
      onPreview={onPreview}
    />
  );
}
